import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import './css/AdminPages.css';

const defaultAllergenForm = {
  key_name: '',
  name_ar: '',
  name_en: '',
  icon: '',
  display_order: 0,
  is_active: true
};

const defaultAdditionForm = {
  addition_header_id: '',
  name_ar: '',
  name_en: '',
  price: '',
  calories: '',
  display_order: 0,
  is_active: true
};

const defaultHeaderForm = {
  name_ar: '',
  name_en: '',
  display_order: 0,
  is_active: true
};

const AdminAdditions = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, isRTL, toggleLanguage } = useLanguage();
  const [allergens, setAllergens] = useState([]);
  const [additionHeaders, setAdditionHeaders] = useState([]);
  const [additions, setAdditions] = useState([]);
  const [headerForm, setHeaderForm] = useState(defaultHeaderForm);
  const [allergenForm, setAllergenForm] = useState(defaultAllergenForm);
  const [additionForm, setAdditionForm] = useState(defaultAdditionForm);
  const [editingHeaderId, setEditingHeaderId] = useState(null);
  const [editingAllergenId, setEditingAllergenId] = useState(null);
  const [editingAdditionId, setEditingAdditionId] = useState(null);
  const [draggingAllergenId, setDraggingAllergenId] = useState(null);
  const [draggingAdditionId, setDraggingAdditionId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });

  const [loadError, setLoadError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoadError('');
      const [allergensRes, additionsRes, headersRes] = await Promise.all([
        axios.get(`${API_URL}/api/allergens/all`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/additions/all`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/additions/headers/all`, { headers: getHeaders() })
      ]);
      setAllergens(allergensRes.data || []);
      setAdditions(additionsRes.data || []);
      setAdditionHeaders(headersRes.data || []);
    } catch (error) {
      console.error('Error loading allergens/additions:', error);
      setLoadError(error.response?.data?.message || '__LOAD_ERROR__');
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const submitHeader = async (e) => {
    e.preventDefault();
    try {
      if (editingHeaderId) {
        await axios.put(`${API_URL}/api/additions/headers/${editingHeaderId}`, headerForm, { headers: getHeaders() });
      } else {
        await axios.post(`${API_URL}/api/additions/headers`, headerForm, { headers: getHeaders() });
      }
      setHeaderForm(defaultHeaderForm);
      setEditingHeaderId(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || t('admin.errorSavingAddition'));
    }
  };

  const submitAllergen = async (e) => {
    e.preventDefault();
    try {
      if (editingAllergenId) {
        await axios.put(`${API_URL}/api/allergens/${editingAllergenId}`, allergenForm, { headers: getHeaders() });
      } else {
        await axios.post(`${API_URL}/api/allergens`, allergenForm, { headers: getHeaders() });
      }
      setAllergenForm(defaultAllergenForm);
      setEditingAllergenId(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || t('admin.errorSavingAllergen'));
    }
  };

  const submitAddition = async (e) => {
    e.preventDefault();
    try {
      if (editingAdditionId) {
        await axios.put(`${API_URL}/api/additions/${editingAdditionId}`, additionForm, { headers: getHeaders() });
      } else {
        await axios.post(`${API_URL}/api/additions`, additionForm, { headers: getHeaders() });
      }
      setAdditionForm(defaultAdditionForm);
      setEditingAdditionId(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || t('admin.errorSavingAddition'));
    }
  };

  const reorderItems = (items, draggedId, targetId) => {
    const fromIndex = items.findIndex((item) => item.id === draggedId);
    const toIndex = items.findIndex((item) => item.id === targetId);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return items;

    const next = [...items];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next.map((item, index) => ({ ...item, display_order: index }));
  };

  const persistAllergenOrder = async (ordered) => {
    await Promise.all(
      ordered.map((item, index) =>
        axios.put(
          `${API_URL}/api/allergens/${item.id}`,
          {
            key_name: item.key_name,
            name_ar: item.name_ar,
            name_en: item.name_en,
            icon: item.icon || '',
            is_active: item.is_active,
            display_order: index
          },
          { headers: getHeaders() }
        )
      )
    );
  };

  const persistAdditionOrder = async (ordered) => {
    await Promise.all(
      ordered.map((item, index) =>
        axios.put(
          `${API_URL}/api/additions/${item.id}`,
          {
            addition_header_id: item.addition_header_id || null,
            name_ar: item.name_ar,
            name_en: item.name_en,
            price: item.price,
            calories: item.calories || 0,
            is_active: item.is_active,
            display_order: index
          },
          { headers: getHeaders() }
        )
      )
    );
  };

  const handleDropAllergen = async (targetId) => {
    if (!draggingAllergenId || savingOrder) return;
    const ordered = reorderItems(allergens, draggingAllergenId, targetId);
    setDraggingAllergenId(null);
    if (ordered === allergens) return;

    setAllergens(ordered);
    setSavingOrder(true);
    try {
      await persistAllergenOrder(ordered);
    } catch (error) {
      alert(error.response?.data?.message || t('admin.errorSavingAllergen'));
      fetchData();
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDropAddition = async (targetId) => {
    if (!draggingAdditionId || savingOrder) return;
    const ordered = reorderItems(additions, draggingAdditionId, targetId);
    setDraggingAdditionId(null);
    if (ordered === additions) return;

    setAdditions(ordered);
    setSavingOrder(true);
    try {
      await persistAdditionOrder(ordered);
    } catch (error) {
      alert(error.response?.data?.message || t('admin.errorSavingAddition'));
      fetchData();
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <div className={`admin-page ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="admin-header">
        <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
          <ArrowLeftIcon className="icon" />
          {t('admin.back')}
        </button>
        <h1>{t('admin.allergensAdditions')}</h1>
        <div className="header-actions">
          <button onClick={toggleLanguage} className="language-toggle-btn">
            {language === 'ar' ? 'EN' : 'AR'}
          </button>
        </div>
      </div>

      <div className="admin-table-container" style={{ marginBottom: '20px' }}>
        {loadError ? (
          <div style={{ marginBottom: '12px', color: '#b91c1c', fontWeight: 600 }}>
            {loadError === '__LOAD_ERROR__' ? t('admin.errorLoadingData') : loadError}
          </div>
        ) : null}
        <h2 style={{ marginBottom: '10px' }}>{t('admin.manageAllergens')}</h2>
        <form onSubmit={submitAllergen}>
          <div className="form-group"><label>{t('admin.key')}</label><input value={allergenForm.key_name} onChange={(e) => setAllergenForm({ ...allergenForm, key_name: e.target.value })} /></div>
          <div className="form-group"><label>{t('admin.nameAr')} *</label><input required value={allergenForm.name_ar} onChange={(e) => setAllergenForm({ ...allergenForm, name_ar: e.target.value })} /></div>
          <div className="form-group"><label>{t('admin.nameEn')} *</label><input required value={allergenForm.name_en} onChange={(e) => setAllergenForm({ ...allergenForm, name_en: e.target.value })} /></div>
          <div className="form-group"><label>{t('admin.icon')}</label><input value={allergenForm.icon} onChange={(e) => setAllergenForm({ ...allergenForm, icon: e.target.value })} placeholder="🥜" /></div>
          <div className="form-group"><label>{t('admin.order')}</label><input type="number" value={allergenForm.display_order} onChange={(e) => setAllergenForm({ ...allergenForm, display_order: parseInt(e.target.value, 10) || 0 })} /></div>
          <div className="form-group"><label><input type="checkbox" checked={allergenForm.is_active} onChange={(e) => setAllergenForm({ ...allergenForm, is_active: e.target.checked })} /> {t('admin.active')}</label></div>
          <button type="submit" className="save-btn">{editingAllergenId ? t('admin.update') : t('admin.add')} {t('admin.allergen')}</button>
        </form>
        <table className="admin-table" style={{ marginTop: '14px' }}>
          <thead><tr><th>↕</th><th>{t('admin.id')}</th><th>{t('admin.order')}</th><th>{t('admin.nameAr')}</th><th>{t('admin.nameEn')}</th><th>{t('admin.icon')}</th><th>{t('admin.active')}</th><th>{t('admin.actions')}</th></tr></thead>
          <tbody>
            {allergens.map((item) => (
              <tr
                key={item.id}
                draggable={!savingOrder}
                onDragStart={() => setDraggingAllergenId(item.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDropAllergen(item.id)}
                style={{ cursor: savingOrder ? 'wait' : 'grab', opacity: draggingAllergenId === item.id ? 0.55 : 1 }}
              >
                <td>⋮⋮</td>
                <td>{item.id}</td>
                <td>{item.display_order ?? 0}</td>
                <td>{item.name_ar}</td>
                <td>{item.name_en}</td>
                <td>{item.icon || '-'}</td>
                <td>{item.is_active ? t('admin.yes') : t('admin.no')}</td>
                <td>
                  <button className="edit-btn" onClick={() => { setEditingAllergenId(item.id); setAllergenForm({ ...item }); }}>{t('admin.edit')}</button>
                  <button className="delete-btn" onClick={async () => { if (window.confirm(t('admin.deleteAllergenConfirm'))) { await axios.delete(`${API_URL}/api/allergens/${item.id}`, { headers: getHeaders() }); fetchData(); } }}>{t('admin.delete')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="admin-table-container">
        <h2 style={{ marginBottom: '10px' }}>{t('admin.addition')} {t('admin.category')}</h2>
        <form onSubmit={submitHeader}>
          <div className="form-group"><label>{t('admin.nameAr')} *</label><input required value={headerForm.name_ar} onChange={(e) => setHeaderForm({ ...headerForm, name_ar: e.target.value })} /></div>
          <div className="form-group"><label>{t('admin.nameEn')} *</label><input required value={headerForm.name_en} onChange={(e) => setHeaderForm({ ...headerForm, name_en: e.target.value })} /></div>
          <div className="form-group"><label>{t('admin.order')}</label><input type="number" value={headerForm.display_order} onChange={(e) => setHeaderForm({ ...headerForm, display_order: parseInt(e.target.value, 10) || 0 })} /></div>
          <div className="form-group"><label><input type="checkbox" checked={headerForm.is_active} onChange={(e) => setHeaderForm({ ...headerForm, is_active: e.target.checked })} /> {t('admin.active')}</label></div>
          <button type="submit" className="save-btn">{editingHeaderId ? t('admin.update') : t('admin.add')}</button>
        </form>
        <table className="admin-table" style={{ marginTop: '14px', marginBottom: '20px' }}>
          <thead><tr><th>{t('admin.id')}</th><th>{t('admin.order')}</th><th>{t('admin.nameAr')}</th><th>{t('admin.nameEn')}</th><th>{t('admin.active')}</th><th>{t('admin.actions')}</th></tr></thead>
          <tbody>
            {additionHeaders.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.display_order ?? 0}</td>
                <td>{item.name_ar}</td>
                <td>{item.name_en}</td>
                <td>{item.is_active ? t('admin.yes') : t('admin.no')}</td>
                <td>
                  <button className="edit-btn" onClick={() => { setEditingHeaderId(item.id); setHeaderForm({ ...item }); }}>{t('admin.edit')}</button>
                  <button className="delete-btn" onClick={async () => { if (window.confirm(t('admin.deleteConfirm'))) { await axios.delete(`${API_URL}/api/additions/headers/${item.id}`, { headers: getHeaders() }); fetchData(); } }}>{t('admin.delete')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <h2 style={{ marginBottom: '10px' }}>{t('admin.manageFixedAdditions')}</h2>
        <form onSubmit={submitAddition}>
          <div className="form-group">
            <label>{t('admin.category')}</label>
            <select value={additionForm.addition_header_id} onChange={(e) => setAdditionForm({ ...additionForm, addition_header_id: e.target.value })} className="form-select">
              <option value="">--</option>
              {additionHeaders.map((h) => (
                <option key={h.id} value={h.id}>{h.name_en} / {h.name_ar}</option>
              ))}
            </select>
          </div>
          <div className="form-group"><label>{t('admin.nameAr')} *</label><input required value={additionForm.name_ar} onChange={(e) => setAdditionForm({ ...additionForm, name_ar: e.target.value })} /></div>
          <div className="form-group"><label>{t('admin.nameEn')} *</label><input required value={additionForm.name_en} onChange={(e) => setAdditionForm({ ...additionForm, name_en: e.target.value })} /></div>
          <div className="form-group"><label>{t('admin.price')} *</label><input required type="number" step="0.01" value={additionForm.price} onChange={(e) => setAdditionForm({ ...additionForm, price: e.target.value })} /></div>
          <div className="form-group"><label>{t('admin.calories')}</label><input type="number" value={additionForm.calories} onChange={(e) => setAdditionForm({ ...additionForm, calories: e.target.value })} /></div>
          <div className="form-group"><label>{t('admin.order')}</label><input type="number" value={additionForm.display_order} onChange={(e) => setAdditionForm({ ...additionForm, display_order: parseInt(e.target.value, 10) || 0 })} /></div>
          <div className="form-group"><label><input type="checkbox" checked={additionForm.is_active} onChange={(e) => setAdditionForm({ ...additionForm, is_active: e.target.checked })} /> {t('admin.active')}</label></div>
          <button type="submit" className="save-btn">{editingAdditionId ? t('admin.update') : t('admin.add')} {t('admin.addition')}</button>
        </form>
        <table className="admin-table" style={{ marginTop: '14px' }}>
          <thead><tr><th>↕</th><th>{t('admin.id')}</th><th>{t('admin.order')}</th><th>{t('admin.category')}</th><th>{t('admin.nameAr')}</th><th>{t('admin.nameEn')}</th><th>{t('admin.price')}</th><th>{t('admin.calories')}</th><th>{t('admin.active')}</th><th>{t('admin.actions')}</th></tr></thead>
          <tbody>
            {additions.map((item) => (
              <tr
                key={item.id}
                draggable={!savingOrder}
                onDragStart={() => setDraggingAdditionId(item.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDropAddition(item.id)}
                style={{ cursor: savingOrder ? 'wait' : 'grab', opacity: draggingAdditionId === item.id ? 0.55 : 1 }}
              >
                <td>⋮⋮</td>
                <td>{item.id}</td>
                <td>{item.display_order ?? 0}</td>
                <td>{item.header_name_ar || '-'}</td>
                <td>{item.name_ar}</td>
                <td>{item.name_en}</td>
                <td>{item.price}</td>
                <td>{item.calories || 0}</td>
                <td>{item.is_active ? t('admin.yes') : t('admin.no')}</td>
                <td>
                  <button className="edit-btn" onClick={() => { setEditingAdditionId(item.id); setAdditionForm({ ...item, addition_header_id: item.addition_header_id || '' }); }}>{t('admin.edit')}</button>
                  <button className="delete-btn" onClick={async () => { if (window.confirm(t('admin.deleteAdditionConfirm'))) { await axios.delete(`${API_URL}/api/additions/${item.id}`, { headers: getHeaders() }); fetchData(); } }}>{t('admin.delete')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminAdditions;
